'use client';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import { debounce } from 'lodash';
import { FacilityQueryContextEnum } from 'parkchirp-shared/enums';
import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';

import { GET_FACILITIES_V2 } from '../../queries';
import { FacilityV2 } from '../../types';

type Option = FacilityV2 & {
	label: string;
	value: string;
	key: string;
};

interface FacilityAutocompleteProps {
	facilityNumber?: string;
	facilityName?: string;
	facilityId?: string;
	organizationId?: number;
	setOrganizationId?: Dispatch<SetStateAction<number>>;
	allowAll?: boolean;
	label?: string;
	disabled?: boolean;
	disableClearable?: boolean;
	error?: boolean;
	required?: boolean;
	specialFilters?: { propertyName: string; op: string; value: any }[];
	userRole?: string;
	onChange?: (value: Option) => void;
	loadingDependency?: boolean;
	usesDefaultRate?: boolean;
	allowMultiple?: boolean;
	onMultipleChange?: (value: Option[]) => void;
	selectedFacilities?: Option[];
	integration?: string;
	isForReports?: boolean;
	usesDefaultRates?: boolean;
	size?: 'small' | 'medium';
	maxActiveVehicles?: number;
	GMPBarcodeRequired?: boolean;
	minActiveVehicles?: number;
}

export const FacilityAutocomplete: FC<FacilityAutocompleteProps> = ({
	facilityName,
	facilityNumber,
	facilityId,
	organizationId,
	setOrganizationId,
	allowAll = false,
	label,
	disabled = false,
	disableClearable = false,
	error = false,
	required = false,
	specialFilters,
	onChange,
	userRole,
	loadingDependency = false,
	usesDefaultRate = false,
	allowMultiple = false,
	onMultipleChange,
	selectedFacilities,
	integration,
	isForReports = false,
	usesDefaultRates = false,
	maxActiveVehicles = null,
	GMPBarcodeRequired = false,
	minActiveVehicles = null,
	size = 'medium',
}) => {
	const allOption: Partial<Option> = useMemo(() => {
		if (allowAll) {
			return {
				label: 'All',
				value: 'All',
				key: 'all',
				facilityNumber: 0,
			};
		}

		return {
			label: 'Start Typing...',
			value: 'None',
			key: 'none',
			facilityNumber: 0,
		};
	}, [allowAll]);

	const defaultOption: Partial<Option> = useMemo(() => {
		if (!facilityId) {
			return allOption;
		}

		let defaultOptionLabel = facilityName;
		if (facilityNumber) {
			defaultOptionLabel += ` (${facilityNumber})`;
		}

		return {
			label: defaultOptionLabel,
			value: facilityId,
			key: facilityId,
			facilityNumber: parseInt(facilityNumber, 10),
			integration,
			usesDefaultRates,
			maxActiveVehicles,
			GMPBarcodeRequired,
			minActiveVehicles,
		};
	}, [
		facilityId,
		facilityName,
		facilityNumber,
		allOption,
		integration,
		usesDefaultRates,
		maxActiveVehicles,
		GMPBarcodeRequired,
		minActiveVehicles,
	]);

	const [options, setOptions] = useState<Option[]>([defaultOption as Option]);

	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [open, setOpen] = useState(false);
	const [selectedOption, setSelectedOption] = useState<Option>(defaultOption as Option);

	useEffect(() => {
		setSelectedOption(defaultOption as Option);
	}, [defaultOption, setSelectedOption]);

	const debouncedOnChange = useMemo(
		() =>
			debounce((value: string) => {
				if (!value) {
					setLoading(false);
					return;
				}

				setSearchTerm(value);
			}, 800),
		[],
	);

	const onInputChange = (_, value) => {
		// If the value is falsy (via clear button or manually deleting the search term/facility),
		// then reset the organizationId
		if (!value || value === 'None' || value === 'All') {
			if (userRole === 'superadmin') {
				setOrganizationId(null);
			}

			return;
		}

		if (!value.includes(searchTerm)) {
			setLoading(true);
		}
		debouncedOnChange(value);
	};

	const onMultipleInputChange = (_, value) => {
		debouncedOnChange(value);
	};

	const onSelectMultiple = (_, selectedOptions) => {
		if (!selectedOptions) return;

		const limitedOptions = selectedOptions.length > 3 ? selectedOptions.slice(0, 3) : selectedOptions;

		if (selectedFacilities?.length === 3) {
			setOpen(false);
		}

		onMultipleChange(limitedOptions);
	};

	const onAutocompleteChange = (option: Option) => {
		if (userRole === 'superadmin' && option?.organizationId) {
			setOrganizationId(option.organizationId);
		}

		setSelectedOption(option);
		onChange(option);
	};

	const getQueryFilters = () => {
		let filters: any = [
			{
				propertyName: 'facilityName',
				op: 'contains',
				value: `${searchTerm}`,
			},
			{
				propertyName: 'facilityNumber',
				op: 'contains',
				value: `${searchTerm}`,
			},
		];
		if (usesDefaultRate) {
			filters.push({
				propertyName: 'usesDefaultRates',
				op: 'equals',
				value: true,
			});
		}

		if (organizationId && organizationId !== 0) {
			filters.push({
				propertyName: 'organizationId',
				op: 'equals',
				value: `${organizationId}`,
			});
		}

		if (specialFilters?.length) {
			filters = [...filters, ...specialFilters];
		}

		return filters;
	};

	const { loading: facilitiesLoading } = useQuery(GET_FACILITIES_V2, {
		variables: {
			offset: 0,
			limit: 10,
			queryContext: FacilityQueryContextEnum.AUTOCOMPLETE,
			filters: getQueryFilters(),
			order: {
				field: 'facilityName',
				sort: 'asc',
			},
		},
		skip: !searchTerm || searchTerm === 'None' || searchTerm === 'All' || disabled,
		onCompleted: (data) => {
			const newOptions: Option[] = data.listFacilities.map((facility) => ({
				...facility,
				label: `${facility.facilityName} (${facility.facilityNumber})`,
				value: facility.id,
				key: facility.id,
				facilityNumber: facility.facilityNumber,
				organizationId: facility.organizationId,
				contractType: facility.contractType,
				organizationName: facility?.organizationSettings?.name,
				requireProductAndRateCodes: facility?.organizationSettings?.requireProductAndRateCodes,
				cityId: facility?.cityId,
				usesDefaultRates: facility?.facilitySettings?.usesDefaultRates,
				maxActiveVehicles: facility?.facilitySettings?.maxActiveVehicles,
				GMPBarcodeRequired: facility?.accessSystemCredentials?.barcodeRequired,
				minActiveVehicles: facility?.facilitySettings?.minActiveVehicles,
			}));
			setOptions([allOption as unknown as Option, ...newOptions]);
			setLoading(false);
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	const isLoading = loading || facilitiesLoading || loadingDependency;

	if (allowMultiple && !isForReports) {
		return (
			<div>
				<Autocomplete
					multiple
					options={options}
					getOptionLabel={(option) => option.label}
					defaultValue={[]}
					open={open}
					onOpen={() => {
						if (selectedFacilities?.length === 3) {
							setOpen(false);
						} else {
							setOpen(true);
						}
					}}
					onClose={() => setOpen(false)}
					isOptionEqualToValue={(option, value) => option.label === value.label}
					onInputChange={onMultipleInputChange}
					onChange={onSelectMultiple}
					loading={loading}
					renderInput={(params) => (
						<TextField
							{...params}
							variant='standard'
							label='Facilities'
							placeholder='Facilities'
							disabled={selectedFacilities?.length === 3}
						/>
					)}
					size={size}
				/>
				<p style={{ marginTop: '10px' }}>Select up to 3 facilities</p>
			</div>
		);
	}
	if (allowMultiple && isForReports) {
		return (
			<div>
				<Autocomplete
					multiple
					options={options}
					value={selectedFacilities}
					getOptionLabel={(option) => option.label}
					defaultValue={[]}
					open={open}
					onOpen={() => {
						if (selectedFacilities?.length === 3) {
							setOpen(false);
						} else {
							setOpen(true);
						}
					}}
					onClose={() => setOpen(false)}
					isOptionEqualToValue={(option, value) => option.label === value.label}
					onInputChange={onMultipleInputChange}
					onChange={onSelectMultiple}
					loading={loading}
					renderInput={(params) => (
						<TextField {...params} variant='outlined' label='Facilities' placeholder='Facilities' />
					)}
					renderTags={() => null}
					size={size}
				/>
			</div>
		);
	}

	return (
		<Autocomplete
			open={open}
			onOpen={() => {
				setOpen(true);
			}}
			onClose={() => {
				setOpen(false);
			}}
			value={selectedOption}
			onChange={(_, value) => {
				onAutocompleteChange(value);
			}}
			onInputChange={onInputChange}
			options={options}
			isOptionEqualToValue={(option, value) => option.value === value.value}
			loading={isLoading}
			filterOptions={(x) => x}
			forcePopupIcon={false}
			fullWidth
			renderOption={(props, option) => (
				<li {...props} key={option.key}>
					{option.label}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || ''}
					required={required}
					error={error}
					inputProps={{ ...params.inputProps, 'data-cy': 'facility-autocomplete' }}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color='inherit' size={20} data-cy='loading-indicator' />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			disabled={disabled}
			readOnly={disabled}
			disableClearable={disableClearable}
			sx={{
				'& .MuiInputBase-root': {
					paddingRight: '39px !important',
				},
			}}
			size={size}
		/>
	);
};
